/**
 * <sticky-atc> — fixed add-to-cart bar that appears once the main buy buttons scroll out of view.
 * Lives inside <product-info>; listens to its `product:changed` event for price / selection / availability.
 * Attributes: data-target = CSS selector of the element to watch (default [data-buy-buttons]).
 * The Add button asks the page to submit the main form (`sticky-atc:add`, bubbled to the owning <product-info> only).
 */
class StickyAtc extends HTMLElement {
  #abort = new AbortController();
  #observer = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.info = this.closest('product-info');
    this.totalEl = this.querySelector('[data-sticky-total]');
    this.selectionEl = this.querySelector('[data-sticky-selection]');
    this.button = this.querySelector('[data-sticky-add]');
    this.hidden = false;
    this.setAttribute('aria-hidden', 'true');
    this.inert = true;

    if (this.info) {
      this.info.addEventListener('product:changed', (event) => this.#update(event.detail), { signal });
      if (this.info.state) this.#update(this.info.state);
    }

    this.button?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('sticky-atc:add', { bubbles: true }));
    }, { signal });

    const target = this.info?.querySelector(this.dataset.target || '[data-buy-buttons]');
    if (target && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(([entry]) => {
        // Show only after the buy buttons have scrolled above the viewport.
        this.#toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.#observer.observe(target);
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
  }

  #toggle(visible) {
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    this.inert = !visible;
    document.documentElement.classList.toggle('has-sticky-atc', visible);
  }

  #update(detail) {
    if (!detail) return;
    if (this.totalEl) this.totalEl.textContent = detail.totalFormatted;
    if (this.selectionEl) this.selectionEl.textContent = detail.selection || '';
    if (this.button) this.button.disabled = !detail.available;
  }
}

if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
